// packages/core/src/quizzes/content/missedWindow.ts
// ─────────────────────────────────────────────────────────────────────────
// "Did You Miss the Window?" (index engine, 0 to 100, high = more pressure)
//
// Six questions measuring how late and how heavy the situation actually
// is. The payoff is the band copy: honest math against the fear. All
// figures computed at a 5% return after inflation, safe 4% yearly spend:
//
//   $1,500/mo for 20 years (45 → 65)  = $616,551 → about $24,700/yr forever
//   $1,000/mo for 15 years (50 → 65)  = $267,289 → about $10,700/yr forever
//   $500/mo for 10 years              = $77,641  → about $3,100/yr forever
// ─────────────────────────────────────────────────────────────────────────

import type { IndexQuizContent } from '../DiscoveryTypes';

export const MISSED_WINDOW: IndexQuizContent = {
	meta: {
		id: 'missed-window',
		title: 'Did You Miss the Window?',
		hook: 'Starting late feels terrible. But the math disagrees more than your fear does.',
		engine: 'index',
		minutes: 3,
		status: 'live',
	},

	questions: [
		{
			type: 'index',
			id: 'mw-age',
			prompt: 'Your age bracket:',
			options: [
				{ label: '50 or older', points: 10 },
				{ label: '40s', points: 7 },
				{ label: '30s', points: 3 },
				{ label: '20s', points: 0 },
			],
		},
		{
			type: 'index',
			id: 'mw-invested',
			prompt: 'What you have invested so far, measured in years of your spending:',
			options: [
				{ label: 'Nothing, or negative after debt', points: 10 },
				{ label: 'Under one year', points: 7 },
				{ label: 'One to five years', points: 4 },
				{ label: 'More than five years', points: 0 },
			],
		},
		{
			type: 'index',
			id: 'mw-monthly',
			prompt: 'What you could realistically invest each month, starting now:',
			options: [
				{ label: 'Almost nothing', points: 10 },
				{ label: 'A couple hundred dollars', points: 6 },
				{ label: '$500 to $1,500', points: 3 },
				{ label: 'More than $1,500', points: 0 },
			],
		},
		{
			type: 'index',
			id: 'mw-debt',
			prompt: 'Your debt situation:',
			options: [
				{ label: 'High-interest debt, and it is growing', points: 10 },
				{ label: 'Some debt, managed', points: 5 },
				{ label: 'Mortgage only', points: 2 },
				{ label: 'None', points: 0 },
			],
		},
		{
			type: 'index',
			id: 'mw-trajectory',
			prompt: 'Your earning trajectory from here:',
			options: [
				{ label: 'Declining or capped', points: 8 },
				{ label: 'Flat', points: 5 },
				{ label: 'Some room to grow', points: 2 },
				{ label: 'Climbing', points: 0 },
			],
		},
		{
			type: 'index',
			id: 'mw-story',
			prompt: 'The story you currently tell yourself about retirement:',
			options: [
				{ label: 'I assume I work forever.', points: 10 },
				{ label: 'It will be small and late.', points: 6 },
				{ label: 'Later than I hoped, but real.', points: 3 },
				{ label: 'On track.', points: 0 },
			],
		},
	],

	bands: [
		{
			min: 75,
			label: 'The window moved. It did not close.',
			insight:
				'This is the hard band, so here is the honest math. Compounding rewards time or rate, and with less time, rate is your lever: savings rate and income. Even from here the years still count. $500 a month for 10 years at a 5% return after inflation grows to about $77,600, which safely covers about $3,100 a year forever. Small next to a full retirement. Very large next to zero, and it stacks with every other year you add.',
		},
		{
			min: 50,
			label: 'Late, not lost',
			insight:
				'The fear says the door shut in your 20s. The math says otherwise. Starting at 45 with nothing, $1,500 a month at a 5% return after inflation grows to about $617,000 by 65, which safely covers about $24,700 a year forever, on top of anything else you have coming. The window is smaller. It is nowhere near closed.',
		},
		{
			min: 25,
			label: 'Inside the window',
			insight:
				'You are later than the textbook case and earlier than your worry says. At this position the biggest risk is not the calendar. It is spending the next five years feeling behind instead of investing through them. Every year you add now still lands on the steep part of the curve.',
		},
		{
			min: 0,
			label: 'The window is wide open',
			insight:
				'You have the one thing money cannot buy back: time. At this position, ordinary monthly amounts do extraordinary things, because the last doubling is always the biggest and you have room for several. The only real mistake available to you is waiting.',
		},
	],

	cta: {
		label: 'See what your years can still do',
		to: '/simulate',
		blurb: 'The fear is generic. The math is specific. Nisk takes your age, savings, and monthly number and shows exactly what the window still holds for you.',
	},

	shareText:
		'I scored {score}/100 on Nisk\u2019s Did You Miss the Window test. Starting at 45 with nothing, $1,500 a month still grows to about $617,000 by 65. Check your window: https://nisk.co/quizzes/missed-window',
};
